import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabase';
import { processSupabaseError } from '@/utils/supabaseErrors';

export interface Parent {
  id: string;
  family_id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  user_id: string;
}

export const useParent = () => {
  const [parent, setParent] = useState<Parent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchParent = async (retryCount = 0): Promise<void> => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Use array query instead of .single() to avoid PGRST116 errors
        const { data, error } = await supabase
          .from('parents')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          const structuredError = processSupabaseError(error);
          
          // Handle PGRST116 (no rows found) with retry logic
          if (error.code === 'PGRST116' && structuredError.retryable && retryCount < 3) {
            console.log(`Parent data not found, retrying... (attempt ${retryCount + 1}/3)`);
            // Exponential backoff: 500ms, 1s, 2s
            const delay = 500 * Math.pow(2, retryCount);
            setTimeout(() => fetchParent(retryCount + 1), delay);
            return;
          }
          
          throw new Error(structuredError.message);
        }

        // Handle array response
        if (!data || data.length === 0) {
          // No parent record found - this might be expected for some users
          console.log('No parent record found for user:', user.id);
          setParent(null);
        } else if (data.length === 1) {
          // Single parent record found - expected case
          setParent(data[0]);
        } else {
          // Multiple parent records found - unexpected but handle gracefully
          console.warn(`Multiple parent records found for user ${user.id}, using first one`);
          setParent(data[0]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching parent data:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setParent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchParent();
  }, [user]);

  return { parent, loading, error };
};

export default useParent; 